/**
 * 이 파일은 코드젠이 생성해준 것이 아니라 수동으로 작성했습니다.
 */

import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { OrderStatus } from '../../../../../resources/@types/courseSchema';
import { type RequestOptions, antmanFetcher } from '../../../../../resources/Fetcher';

export type Maybe<T> = T | null;

export type OneOf<T> = T extends unknown[]
  ? T[number]
  : T extends readonly unknown[]
    ? T[number]
    : T;

export type Exact<T extends { [key: string]: unknown }> = {
  [K in keyof T]-?: Exclude<T[K], undefined>;
};

export type Values<T extends Record<string, unknown>> = T extends Record<string, infer U>
  ? U
  : T extends { [key: string]: infer U }
    ? U
    : never;

export const enum ResponseStatus {
  OK = 'OK',
  SERVER_ERROR = 'SERVER_ERROR',
  DUPLICATE = 'DUPLICATE',
  NOT_FOUND = 'NOT_FOUND',
  BAD_REQUEST = 'BAD_REQUEST',
  BAD_PARAMETER = 'BAD_PARAMETER',
  CONFLICT = 'CONFLICT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
}

export type Success<T = unknown> = {
  statusCode: ResponseStatus.OK;
  message: '';
  data: T;
};

export type AntManSuccess<T = unknown> = {
  ok: true;
} & T;

export type Error = {
  statusCode: Omit<keyof typeof ResponseStatus, 'OK'>;
  message: string;
  data: Maybe<''>;
};

export type AntManError = {
  ok: false;
  msg: string;
  statusCode?: string;
};

/**
 * @typeParam T - Response data type
 */
export type ApiResponse<T = unknown> = Success<T>;

export type AntManApiResponse<T = unknown> = AntManSuccess<T>;

export type Order = {
  id: number;
  createdAt: string | null;
  status: keyof typeof OrderStatus;
  title: string | null;
  paymentPrice: number | null;
  regularPrice: number | null;
  receiptUrl: string | null;
  itemsCount: number;
  currency: string;
};

export type Orders = { orders: Order[] };

export type V1OrdersResponse = AntManApiResponse<Orders>;

export async function fetchV1Orders(url: string, requestOptions?: RequestOptions) {
  const response = await antmanFetcher.get<V1OrdersResponse>(url, requestOptions);

  return response;
}

export function useV1OrdersQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<UseQueryOptions<V1OrdersResponse, AntManError>, 'queryKey' | 'queryFn'>;
  requestOptions?: RequestOptions;
}) {
  return useQuery<V1OrdersResponse, AntManError>(
    queryKey,
    () => fetchV1Orders(url, requestOptions),
    options,
  );
}

/**
 * 값이 없으면 그냥 필드를 안 내려줍니다.
 */
type Cart = {
  title: string;
  status?: string; // 취소
  quantity?: number;
  price?: number;
  pay_price?: number;
  sum_price?: number;
  tax?: number;
  desc?: string;
};

export type BillStatement = {
  id: string; // string number
  type: 'statement';
  data: {
    filename: string;
    date: string; //"2024년 02월 27일"
    receiver: string;
    carts: Cart[];
    currency: string;
    sum_info: {
      quantity: number;
      price: number;
      sum_price: number;
      tax: number;
    };
    total_price: number;
  };
  total_price: number;
};

export type V1BillStatementOrderResponse = AntManApiResponse<BillStatement>;

export async function fetchV1BillStatementOrder(url: string, requestOptions?: RequestOptions) {
  const response = await antmanFetcher.get<V1BillStatementOrderResponse>(url, requestOptions);

  return response;
}

export function useV1BillStatementOrderQuery({
  queryKey,
  url,
  options,
  requestOptions,
}: {
  queryKey: QueryKey;
  url: string;
  options?: Omit<
    UseQueryOptions<V1BillStatementOrderResponse, AntManError>,
    'queryKey' | 'queryFn'
  >;
  requestOptions?: RequestOptions;
}) {
  return useQuery<V1BillStatementOrderResponse, AntManError>(
    queryKey,
    () => fetchV1BillStatementOrder(url, requestOptions),
    options,
  );
}

export const API_ENDPOINT = {
  V1Orders: `/orders`,
  V1BillStatementOrder: (orderId: number) => `/bill-statements/${orderId}`,
} as const;
